import React, { useState, useEffect } from "react";
import { Row, Col } from "antd";
import { Link } from "react-router-dom";
import { subscribeNewsletter } from "../../services/newsletterService";
import { ARTICLES, CATEGORIE_COLORS } from "../../data/articles";
import "./BlogScreen.css";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;


function CategorieBadge({ categorie }) {
  const style = CATEGORIE_COLORS[categorie] || CATEGORIE_COLORS["Actualité"];
  return (
    <span className="blog-badge" style={{ background: style.bg, color: style.color }}>
      {categorie}
    </span>
  );
}

function BlogCard({ article }) {
  const isSPSTI = article.categorie === "Annuaire SPSTI";
  return (
    <Link to={`/blog/${article.key}`} className="blog-card-link">
      <div className="blog-card">
        <div className="blog-card-img-wrap">
          {article.image ? (
            <img src={article.image} alt={article.titre} className="blog-card-img" />
          ) : (
            <div className="blog-card-img-placeholder">
              {isSPSTI && <span className="blog-card-dept-num">{article.deptNum}</span>}
            </div>
          )}
        </div>
        <div className="blog-card-body">
          <div className="blog-card-meta">
            <CategorieBadge categorie={article.categorie} />
            {isSPSTI && article.deptName
              ? <span className="blog-card-date">{article.deptName}</span>
              : <span className="blog-card-date">{article.date}</span>
            }
          </div>
          <div className="blog-card-heading">
            <h3 className="blog-card-title">{article.titre}</h3>
            <span className="blog-card-arrow">↗</span>
          </div>
          <p className="blog-card-desc">{article.description}</p>
        </div>
      </div>
    </Link>
  );
}

const CATEGORIES = ["Tous", ...Object.keys(CATEGORIE_COLORS)];

function useArticlesPerPage() {
  const getCount = () => {
    if (window.innerWidth < 576) return 4;
    if (window.innerWidth < 992) return 6;
    return 9;
  };
  const [count, setCount] = useState(getCount);
  useEffect(() => {
    const handler = () => setCount(getCount());
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return count;
}

function getPageNumbers(currentPage, totalPages) {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
  const pages = [1];
  if (currentPage > 3) pages.push("...");
  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);
  for (let i = start; i <= end; i++) pages.push(i);
  if (currentPage < totalPages - 2) pages.push("...");
  pages.push(totalPages);
  return pages;
}

function BlogPagination({ currentPage, totalPages, onPageChange }) {
  const pages = getPageNumbers(currentPage, totalPages);
  return (
    <div className="blog-pagination">
      <button
        className="blog-pagination-nav"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M11 14L6 9L11 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span>Page précédente</span>
      </button>
      <div className="blog-pagination-numbers">
        {pages.map((p, i) =>
          p === "..." ? (
            <span key={`ellipsis-${i}`} className="blog-pagination-ellipsis">…</span>
          ) : (
            <button
              key={p}
              className={`blog-pagination-page${currentPage === p ? " blog-pagination-page--active" : ""}`}
              onClick={() => onPageChange(p)}
            >
              {p}
            </button>
          )
        )}
      </div>
      <button
        className="blog-pagination-nav"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        <span>Page suivante</span>
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M7 4L12 9L7 14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
    </div>
  );
}

function BlogScreen() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeCategorie, setActiveCategorie] = useState("Tous");
  const [hoveredCategorie, setHoveredCategorie] = useState(null);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const articlesPerPage = useArticlesPerPage();

  const handleNewsletter = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(email)) { setEmailError("Veuillez saisir une adresse e-mail valide."); return; }
    setEmailError("");
    setLoading(true);
    try {
      await subscribeNewsletter(email);
      setSubmitted(true);
    } catch (err) {
      setEmailError("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="blog-page">

      {/* ── HERO ── */}
      <section className="blog-hero">
        <div className="blog-inner">
          <Row gutter={[64, 40]} align="middle">
            <Col xs={24} lg={14}>
              <span className="blog-eyebrow">Notre blog</span>
              <h1 className="blog-hero-title">Nos activités</h1>
              <p className="blog-hero-text">
                Restez informé de nos actualités, de nos événements et de nos mises à jour.
              </p>
              <div className="blog-search-wrap">
                <svg className="blog-search-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="8.5" cy="8.5" r="5.5" stroke="currentColor" strokeWidth="1.6"/>
                  <path d="M13 13L17 17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                </svg>
                <input
                  type="text"
                  className="blog-search-input"
                  placeholder="Rechercher un article…"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                />
                {search && (
                  <button className="blog-search-clear" onClick={() => { setSearch(""); setCurrentPage(1); }} aria-label="Effacer">✕</button>
                )}
              </div>
            </Col>
            <Col xs={24} lg={10}>
              <div className="blog-newsletter">
                {submitted ? (
                  <p className="blog-newsletter-thanks">
                    Merci ! Vous êtes bien inscrit à notre newsletter.
                  </p>
                ) : (
                  <form className="blog-newsletter-form" onSubmit={handleNewsletter}>
                    <label className="blog-newsletter-label">Inscrivez-vous à notre newsletter</label>
                    <div className="blog-newsletter-row">
                      <input
                        type="email"
                        className="blog-newsletter-input"
                        placeholder="Saisissez votre adresse e-mail"
                        value={email}
                        onChange={(e) => { setEmail(e.target.value); setEmailError(""); }}
                      />
                      <button type="submit" className="blog-newsletter-btn" disabled={loading}>
                        {loading ? "Inscription…" : "S'inscrire"}
                      </button>
                    </div>
                    {emailError && <p className="blog-newsletter-error">{emailError}</p>}
                    <p className="blog-newsletter-hint">
                      Nous respectons la confidentialité de vos données.{" "}
                      <Link to="/politique-de-confidentialite">Politique de confidentialité</Link>.
                    </p>
                  </form>
                )}
              </div>
            </Col>
          </Row>
        </div>
      </section>

      <div className="wave-blog-hero" aria-hidden="true">
        <svg viewBox="0 0 1280 120" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0 120 H1280 V60 C1000 0 300 120 0 40 Z" fill="#f7fbfa" />
        </svg>
      </div>


      {/* ── FILTRES ── */}
      <div className="blog-filters">
        <div className="blog-inner">
          <div className="blog-filters-row">
            {CATEGORIES.map((cat) => {
              const style = cat === "Tous"
                ? { bg: "#04aceb", color: "#ffffff" }
                : CATEGORIE_COLORS[cat] || { bg: "rgba(4, 172, 235, 0.12)", color: "#04aceb" };
              const isActive = activeCategorie === cat;
              const isHovered = hoveredCategorie === cat && !isActive;
              return (
                <button
                  key={cat}
                  className={`blog-filter-btn${isActive ? " blog-filter-btn--active" : ""}`}
                  onClick={() => { setActiveCategorie(cat); setCurrentPage(1); }}
                  onMouseEnter={() => setHoveredCategorie(cat)}
                  onMouseLeave={() => setHoveredCategorie(null)}
                  style={(isActive || isHovered) ? { background: style.bg, borderColor: style.color, color: style.color } : {}}
                >
                  {cat}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── TOUS LES ARTICLES ── */}
      <section className="blog-section">
        <div className="blog-inner">
          {(() => {
            const filtered = ARTICLES
              .filter((a) => {
                // "Tous" exclut l'annuaire SPSTI : il n'apparaît que sous son propre filtre.
                if (activeCategorie === "Tous") return a.categorie !== "Annuaire SPSTI";
                return a.categorie === activeCategorie;
              })
              .filter((a) => {
                const normalize = (s) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[-'']/g, '');
                const q = normalize(search.trim());
                if (!q) return true;
                const text = normalize(a.titre + ' ' + a.description);
                return q.split(/\s+/).every(word => text.includes(word));
              });
            const totalPages = Math.ceil(filtered.length / articlesPerPage);
            const displayed = filtered.slice((currentPage - 1) * articlesPerPage, currentPage * articlesPerPage);
            return (
              <>
                <Row gutter={[32, 32]}>
                  {displayed.map((article) => (
                    <Col key={article.key} xs={24} sm={12} lg={8}>
                      <BlogCard article={article} />
                    </Col>
                  ))}
                </Row>
                {totalPages > 1 && (
                  <BlogPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                  />
                )}
              </>
            );
          })()}
        </div>
      </section>
    </div>
  );
}

export default BlogScreen;
